import { useQuery, useMutation, useQueryClient, type UseMutationOptions } from '@tanstack/react-query';
import { serviceApiV3 } from '@/api/client';
import { mediaBase } from './configs';
import type {
  VodJobsRequest,
  VodJobsResponse,
  VodJobDetailResponse,
  VodCreateJobRequest,
  VodJobStatuses,
} from './vod-jobs-types';

// ============================================
// Query keys
// ============================================

export const vodJobKeys = {
  all: ['vod', 'jobs'] as const,
  list: (serviceId: string, params: VodJobsRequest) =>
    ['vod', 'jobs', 'list', serviceId, params] as const,
  detail: (serviceId: string, jobId: string) =>
    ['vod', 'jobs', 'detail', serviceId, jobId] as const,
  statuses: (serviceId: string) =>
    ['vod', 'statuses', serviceId] as const,
};

// ============================================
// Queries
// ============================================

export function useVodJobs(
  serviceId: string,
  params: VodJobsRequest,
) {
  return useQuery<VodJobsResponse>({
    queryKey: vodJobKeys.list(serviceId, params),
    enabled: !!serviceId,
    staleTime: 5_000,
    refetchInterval: 10_000,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const p: Record<string, string> = {};
      if (params.page !== undefined) p.page = String(params.page);
      if (params.limit !== undefined) p.per_page = String(params.limit);
      if (params.status) p.status_id = params.status;
      if (params.domain) p.domain = params.domain;
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'vod')}/jobs`, { params: p });
      return (
        res.data?.data ??
        res.data ?? {
          jobs: [],
          count: 0,
          total: 0,
          page: 1,
          per_page: params.limit ?? 10,
        }
      );
    },
  });
}

export function useVodJobDetail(
  serviceId: string,
  jobId: string,
) {
  return useQuery<VodJobDetailResponse>({
    queryKey: vodJobKeys.detail(serviceId, jobId),
    enabled: !!serviceId && !!jobId,
    staleTime: 2_000,
    refetchInterval: (query) => {
      const data = query.state.data as VodJobDetailResponse | undefined;
      return data?.job?.status === 'processing' ? 3_000 : 10_000;
    },
    queryFn: async () => {
      const res = await serviceApiV3.get(`${mediaBase(serviceId, 'vod')}/jobs/${jobId}`);
      return res.data?.data ?? res.data;
    },
  });
}

export function useVodStatuses(serviceId: string) {
  return useQuery<VodJobStatuses[]>({
    queryKey: vodJobKeys.statuses(serviceId),
    enabled: !!serviceId,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const res = await serviceApiV3.get(
        `${mediaBase(serviceId, 'vod')}/statuses`,
      );
      return res.data?.data ?? res.data ?? [];
    },
  });
}

// ============================================
// Mutations
// ============================================

export function useCreateVodJob(
  serviceId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseMutationOptions<any, Error, VodCreateJobRequest>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (data: VodCreateJobRequest) => {
      const res = await serviceApiV3.post(`${mediaBase(serviceId, 'vod')}/jobs`, data);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodJobKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

/** Redo (restart) a completed or failed job. */
export function useRedoVodJob(
  serviceId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseMutationOptions<any, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      // Legacy sends a literal empty object body — keep it for server compatibility.
      const res = await serviceApiV3.post(
        `${mediaBase(serviceId, 'vod')}/jobs/${jobId}/redo`,
        {},
      );
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodJobKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}

export function useDeleteVodJob(
  serviceId: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options?: UseMutationOptions<any, Error, string>,
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string) => {
      const res = await serviceApiV3.delete(`${mediaBase(serviceId, 'vod')}/jobs/${jobId}`);
      return res.data?.data ?? res.data;
    },
    ...options,
    onSuccess: (...args) => {
      qc.invalidateQueries({ queryKey: vodJobKeys.all });
      options?.onSuccess?.(...args);
    },
  });
}
